import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

/**
 * "Hele gesprek als notitie" via de server (23 okt 2026, 9856).
 *
 * Vroeger bouwde de browser de notitie zelf (bodies + bijlagen via Graph, dan een POST); sloot
 * je de drawer, dan stopte het, en de ✓ stond alleen in lokale state — na heropenen leek niets
 * bewaard en een tweede klik maakte alles dubbel. Nu start één call een achtergrondtaak in de
 * engine; de status per gesprek komt uit de boekhouding (mail_notitie_gesprek) en deze hook
 * leeft op de projectpagina (ProjectDataContext), zodat hij blijft volgen als de drawer dicht
 * gaat en daarna de notities ververst.
 */
export const POLL_MS = 2000;
export const MAX_VOLGEN_MS = 4 * 60 * 1000;
const LEEG = { gesprekken: [], lopend: [] };

const taakSleutel = (t) => `${t?.gesprek ?? ''}|${t?.sinds ?? ''}`;

/** Status van één gesprek (groep uit groepeerThreads) in de drawer. Puur, voor de tests. */
export function statusVoorGroep(groep, stand = LEEG) {
  const berichten = groep?.berichten || [];
  const ids = berichten.map((m) => String(m.id));
  const convs = berichten.map((m) => m.conversationId).filter(Boolean);
  const raakt = (lijstIds = [], lijstConvs = []) => (
    ids.some((i) => lijstIds.includes(i)) || convs.some((c) => lijstConvs.includes(c))
  );
  // Een los bericht binnen een gesprek toont alleen zijn eigen taak, niet die van het hele gesprek.
  const hoortBij = (t) => (berichten.length === 1
    ? (t.ids || []).length === 1 && String(t.ids[0]) === ids[0]
    : (groep?.key && t.gesprek === groep.key) || raakt((t.ids || []).map(String), []));
  const taken = (stand?.lopend || [])
    .filter(hoortBij)
    .sort((a, b) => String(b.sinds || '').localeCompare(String(a.sinds || '')));
  const taak = taken[0];
  if (taak?.status === 'bezig') return { soort: 'bezig' };
  const rij = (stand?.gesprekken || []).find((g) => g.note_id && raakt(g.ids || [], g.convs || []));
  const opgeslagen = rij ? {
    noteId: rij.note_id,
    datum: rij.bijgewerkt || null,
    nieuw: ids.filter((i) => !(rij.ids || []).includes(i)).length,
  } : null;
  if (taak?.status === 'fout' && (!rij || String(taak.klaar_op || '') > String(rij.bijgewerkt || ''))) {
    return { soort: 'fout', fout: taak.fout || 'Opslaan mislukt', ...(opgeslagen || {}) };
  }
  if (opgeslagen) return { soort: 'klaar', ...opgeslagen };
  return { soort: null };
}

export function useMailNotities(projectId, { onKlaar } = {}) {
  const [stand, setStand] = useState(LEEG);
  const [volgend, setVolgend] = useState(0);   // aantal gestarte taken waarvan we de afloop nog melden
  const gevolgd = useRef(new Map());           // taakSleutel → starttijd (ms)
  const [tik, setTik] = useState(0);           // elke afgeronde poging (ook mislukt) → volgende
  const pollSinds = useRef(0);
  const onKlaarRef = useRef(onKlaar);
  onKlaarRef.current = onKlaar;

  const ruimOp = () => {
    const nu = Date.now();
    gevolgd.current.forEach((sinds, k) => { if (nu - sinds > MAX_VOLGEN_MS) gevolgd.current.delete(k); });
    setVolgend(gevolgd.current.size);
  };

  const laad = useCallback(async () => {
    if (!projectId) return null;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/mail-notities?projectId=${encodeURIComponent(projectId)}`);
      if (!r.ok) return null;   // finally plant de volgende poging
      const d = await r.json();
      const nieuw = { gesprekken: d?.gesprekken || [], lopend: d?.lopend || [] };
      // Afloop van wat wij in deze sessie startten: één melding, en de notities verversen.
      let klaar = false;
      nieuw.lopend.forEach((t) => {
        const k = taakSleutel(t);
        if (!gevolgd.current.has(k) || t.status === 'bezig') return;
        gevolgd.current.delete(k);
        if (t.status === 'klaar') {
          klaar = true;
          toast.success('Gesprek bewaard als notitie');
          (t.waarschuwingen || []).forEach((w) => toast(w, { icon: '⚠️', duration: 8000 }));
        } else if (t.status === 'fout') {
          toast.error(t.fout || 'Opslaan als notitie mislukt — probeer het opnieuw');
        }
      });
      setStand(nieuw);
      if (klaar) onKlaarRef.current?.();
      return nieuw;
    } catch {
      return null;
    } finally {
      ruimOp();
      setTik((t) => t + 1);
    }
  }, [projectId]);

  // Ander project: schone lei. Laden gebeurt lui (Correspondentie roept laad() bij openen),
  // zodat een gewone projectpagina geen extra aanvraag kost.
  useEffect(() => {
    setStand(LEEG);
    gevolgd.current = new Map();
    pollSinds.current = 0;
    setVolgend(0);
  }, [projectId]);

  // Pollen zolang er iets loopt dat wij volgen of dat de server als 'bezig' meldt — ook na
  // een mislukte statusaanvraag (tik), maar nooit langer dan MAX_VOLGEN_MS achter elkaar.
  const bezig = stand.lopend.some((t) => t.status === 'bezig');
  useEffect(() => {
    if (!volgend && !bezig) { pollSinds.current = 0; return undefined; }
    if (!pollSinds.current) pollSinds.current = Date.now();
    if (Date.now() - pollSinds.current > MAX_VOLGEN_MS) return undefined;
    const t = setTimeout(laad, POLL_MS);
    return () => clearTimeout(t);
  }, [volgend, bezig, tik, laad]);

  const start = useCallback(async ({ messageIds, gesprek }) => {
    const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/mail-notities`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectId, messageIds, gesprek }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d?.detail || d?.error || `HTTP ${r.status}`);
    const taak = { status: 'bezig', gesprek: d.gesprek ?? gesprek ?? null, ids: messageIds, sinds: d.sinds };
    gevolgd.current.set(taakSleutel(taak), Date.now());
    pollSinds.current = Date.now();
    setVolgend(gevolgd.current.size);
    setTik((t) => t + 1);
    setStand((prev) => ({
      ...prev,
      lopend: [...prev.lopend.filter((t) => taakSleutel(t) !== taakSleutel(taak)), taak],
    }));
    return d;
  }, [projectId]);

  return { stand, laad, start };
}
